import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const CHART_COLORS = ['#1e85e5', '#8B46D0', '#00BBB2', '#f97316'];

const aggregateForPieChart = (data, topCount = 4) => {
    const sorted = [...data].sort((a, b) => Number(b.amount) - Number(a.amount));

    if (sorted.length <= topCount + 1) {
        return sorted.map((item, idx) => ({
            name: item.name,
            value: Number(item.amount),
            color: CHART_COLORS[idx % CHART_COLORS.length]
        }));
    }

    const topItems = sorted.slice(0, topCount);
    const others = sorted.slice(topCount);
    const othersTotal = others.reduce((sum, item) => sum + Number(item.amount), 0);

    return [
        ...topItems.map((item, idx) => ({
            name: item.name,
            value: Number(item.amount),
            color: CHART_COLORS[idx]
        })),
        { name: `Others (${others.length})`, value: othersTotal, color: '#9CA3AF' }
    ];
};

const HorizontalBarList = ({ data, maxItems = 5, formatCurrency, viewMode = 'bar' }) => {
    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                No client data available
            </div>
        );
    }

    const sorted = [...data]
        .sort((a, b) => Number(b.amount) - Number(a.amount))
        .slice(0, maxItems);
    const maxAmount = Number(sorted[0]?.amount) || 1;

    const pieData = aggregateForPieChart(data, 4);

    return (
        <div>
            {viewMode === 'bar' ? (
                <div className="space-y-2.5">
                    {sorted.map((item, idx) => {
                        const percentage = (Number(item.amount) / maxAmount) * 100;
                        return (
                            <div key={item.policy_Id || idx} className="flex items-center gap-2 sm:gap-3">
                                {/* name with instant tooltip */}
                                <span className="relative group cursor-default w-20 sm:w-36 flex-shrink-0">
                                    <span className="text-sm sm:text-base text-gray-700 truncate block">
                                        {item.name}
                                    </span>
                                    {/* instant tooltip */}
                                    <span className="absolute bottom-full left-0 mb-2 px-2.5 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-100 z-50 pointer-events-none">
                                        {item.name}
                                        <span className="absolute top-full left-4 border-4 border-transparent border-t-gray-900" />
                                    </span>
                                </span>
                                {/* bar - reduced height */}
                                <div className="flex-1 h-2.5 sm:h-3 bg-primary/20 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-primary to-primary-light rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                                <span className="w-16 sm:w-24 text-xs sm:text-base font-medium text-gray-900 text-right tabular-nums flex-shrink-0">
                                    {formatCurrency(item.amount)}
                                </span>
                            </div>
                        );
                    })}
                    {/* others row when there are more items */}
                    {data.length > maxItems && (() => {
                        const others = [...data]
                            .sort((a, b) => Number(b.amount) - Number(a.amount))
                            .slice(maxItems);
                        const othersTotal = others.reduce((sum, item) => sum + Number(item.amount), 0);
                        const percentage = (othersTotal / maxAmount) * 100;
                        return (
                            <div className="flex items-center gap-2 sm:gap-3">
                                <span className="w-20 sm:w-36 text-sm sm:text-base text-gray-400 flex-shrink-0">
                                    Others ({others.length})
                                </span>
                                <div className="flex-1 h-2.5 sm:h-3 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gray-300 rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                                <span className="w-16 sm:w-24 text-xs sm:text-base font-medium text-gray-500 text-right tabular-nums flex-shrink-0">
                                    {formatCurrency(othersTotal)}
                                </span>
                            </div>
                        );
                    })()}
                </div>
            ) : (
                /* pie chart view */
                <div className="flex items-center gap-4 sm:gap-6">
                    <div className="w-28 h-28 sm:w-36 sm:h-36 flex-shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    innerRadius={30}
                                    outerRadius={55}
                                    paddingAngle={2}
                                    dataKey="value"
                                    strokeWidth={0}
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value) => formatCurrency(value)}
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        fontSize: '12px'
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    {/* legend */}
                    <div className="flex-1 space-y-2">
                        {pieData.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span
                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: item.color }}
                                    />
                                    <span className="text-gray-700 truncate">
                                        {item.name}
                                    </span>
                                </div>
                                <span className="font-medium text-gray-900 tabular-nums ml-2 flex-shrink-0">
                                    {formatCurrency(item.value)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HorizontalBarList;
